import { ReactElement, useState } from 'react';
import {
  getEffectiveDiscount,
  isDiscountApplicable,
  formatMonetaryAmount,
  getPlanEstimations,
  formatTaxableText,
} from '@localstack/services';
import { Plan, PlansDurationType, OrderContext, TaxDetails, PlanFamily, Subscription } from '@localstack/types';
import {
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Radio,
  ListItemText,
  Box,
  Typography,
  Divider,
  styled,
} from '@mui/material';

import { ProductsList } from '../../ProductsList';
import { BillingIntervalToggle } from '../../../../display';

import { ServiceCoverageLink } from '../../ServiceCoverageLink';

const DEFAULT_SELECTED_INTERVAL = PlansDurationType.YEARLY;

const purpose = (family: string): string => {
  if (family === PlanFamily.TRIAL_PLANS) return 'for evaluation';
  if (family === PlanFamily.HOBBY_PLANS) return 'for non-commercial use';
  if (family === PlanFamily.PRO_PLANS) return 'for individuals';
  if (family === PlanFamily.TEAM_PLANS) return 'for teams';
  if (family === PlanFamily.BASE_PLANS) return '55+ AWS Services';
  if (family === PlanFamily.ULTIMATE_PLANS) return '110+ AWS Services';
  return '';
};

const TrialListItemButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  '& div': { color: theme.palette.success.main },
  '&::before': {
    content: '" "',
    position: 'absolute',
    left: 0,
    height: '100%',
    width: '10px',
    background: theme.palette.success.main,
  },
}));

interface PlanListItemProps {
  planVariants: Plan[];
  subscription?: Optional<Subscription>;
  groupIndex: number;
  totalGroups: number;
  selected: Optional<Plan>;
  context: OrderContext;
  isAdminMode: boolean;
  tax: Optional<TaxDetails>;
  onSelect?: (plan: Plan) => unknown;
}

export const PlanListItem = ({
  planVariants,
  subscription,
  groupIndex,
  totalGroups,
  selected,
  context,
  isAdminMode,
  tax,
  onSelect,
}: PlanListItemProps): ReactElement => {
  const [selectedInterval, setSelectedInterval] = useState<PlansDurationType>(
    () => planVariants.find((v) => v.id === selected?.id)?.interval_months ?? DEFAULT_SELECTED_INTERVAL,
  );

  const plan = planVariants.find((p) => p.interval_months === selectedInterval) ?? planVariants[0];
  if (!plan) return <></>;

  const planPurpose = purpose(plan.family);
  const effective = getEffectiveDiscount({ subscription, plan });
  const discountForPlan = isAdminMode ? effective.discount : effective.renewalDiscount;

  const billingIntervalOnChange = (interval: PlansDurationType) => {
    const planVariant = planVariants.find((v) => v.interval_months === interval);
    if (!planVariant) return;

    onSelect?.(planVariant);
    setSelectedInterval(interval);
  };

  const ItemComponent = plan.family === PlanFamily.TRIAL_PLANS ? TrialListItemButton : ListItemButton;

  return (
    <>
      <ItemComponent onClick={() => onSelect?.(plan)} alignItems="flex-start">
        <ListItemAvatar>
          <Radio color="primary" value={plan.id} checked={planVariants.some((v) => selected?.id === v.id)} />
        </ListItemAvatar>
        <ListItemText
          primaryTypographyProps={{ component: 'div' }}
          secondaryTypographyProps={{ component: 'div' }}
          primary={
            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
              <Box>{plan.name}</Box>
              <Box>
                {discountForPlan && isDiscountApplicable(discountForPlan) && (
                  <>
                    <s>
                      {formatMonetaryAmount(
                        getPlanEstimations(plan.products, context, discountForPlan, false) / plan.interval_months,
                        plan.currency,
                      )}
                    </s>{' '}
                  </>
                )}
                {formatMonetaryAmount(
                  getPlanEstimations(plan.products, context, discountForPlan) / plan.interval_months,
                  plan.currency,
                )}
                <Typography variant="caption"> {formatTaxableText('', tax, false)}</Typography>
              </Box>
            </Box>
          }
          secondary={
            <>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                <Box>
                  {planPurpose && (
                    <>
                      <strong>{planPurpose}</strong> ·
                    </>
                  )}
                  <ServiceCoverageLink emulator={plan.emulator} />
                </Box>
                <Box>per month {plan.interval_months === 12 && <span>(billed annually)</span>}</Box>
              </Box>
              {planVariants.length > 1 ? (
                <Box display="flex" justifyContent="flex-end">
                  <BillingIntervalToggle selectedInterval={selectedInterval} onChange={billingIntervalOnChange} />
                </Box>
              ) : undefined}
            </>
          }
        />
      </ItemComponent>
      {selected?.id === plan.id && (
        <ListItem>
          <ListItemAvatar>
            <div />
          </ListItemAvatar>
          <ListItemText disableTypography>
            <ProductsList plan={plan} products={plan.products} context={context} isAdminMode={isAdminMode} />
          </ListItemText>
        </ListItem>
      )}
      {groupIndex < totalGroups - 1 && <Divider variant="fullWidth" component="li" />}
    </>
  );
};
